// dependencies
import { useEffect } from "react";

export interface UseOverlayFocusOptions<T extends HTMLElement = HTMLElement> {
	/**
	 * Whether the overlay is open
	 */
	isOpen: boolean;
	/**
	 * Whether the overlay should be rendered in DOM
	 */
	shouldRender: boolean;
	/**
	 * Whether the portal is rendered and ready
	 */
	isRendered: boolean;
	/**
	 * Ref to the element that should receive focus
	 */
	elementRef: React.RefObject<T | null>;
}

/**
 * Hook for managing focus in overlay components.
 *
 * This hook handles the common pattern of setting focus to an overlay
 * element when it becomes visible and ready. It ensures focus is only
 * set when all conditions are met:
 * - The overlay is open
 * - The overlay is mounted in the DOM
 * - The portal is rendered and ready
 * - The target element exists
 *
 * ## Features
 * - **Accessibility compliant**: Follows WCAG guidelines for focus management
 * - **Safe focus setting**: Only sets focus when element is ready
 * - **Automatic cleanup**: No manual cleanup required
 *
 * ## Usage
 * ```tsx
 * const Component = ({ isOpen }) => {
 *   const elementRef = useRef<HTMLDivElement>(null);
 *   const { shouldRender, isAnimating } = useOverlayAnimation({ isOpen });
 *   const { portalRef, isRendered } = usePortal({ shouldRender });
 *
 *   useOverlayFocus({
 *     isOpen,
 *     shouldRender,
 *     isRendered,
 *     elementRef,
 *   });
 *
 *   return isRendered ? createPortal(
 *     <div ref={elementRef} tabIndex={-1}>Content</div>,
 *     portalRef.current
 *   ) : null;
 * };
 * ```
 *
 * @param options - Configuration options for focus management
 */
export const useOverlayFocus = <T extends HTMLElement = HTMLElement>({
	isOpen,
	shouldRender,
	isRendered,
	elementRef,
}: UseOverlayFocusOptions<T>): void => {
	// Set focus to overlay when opened for accessibility
	useEffect(() => {
		if (isOpen && shouldRender && isRendered && elementRef.current) {
			elementRef.current.focus();
		}
	}, [isOpen, shouldRender, isRendered, elementRef]);
};
